import type { DashboardHealthDto, ExpenseCategoryValue, MemberDto } from "@gladis/shared";
import { formatEur } from "@gladis/shared";
import { FunnelChart, overspendOf, type FunnelStage } from "@gladis/ui";
import { expenseCategoryLabel, useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import { SectionCard } from "../section-card.js";
import { filterSummary, type HealthFilterState } from "./health-filters.js";

/**
 * Where the money went — income at the top, each category taken out, what is left at the bottom.
 *
 * ==========================================================================================
 * IT READS FROM ONE PAYLOAD, WHICH IS THE POINT
 * ==========================================================================================
 *
 * Income, the whole of `Out` and the categories all come from the health read. `expenseSummary.totalCents`
 * IS the `Out` card's figure — one number grouped from one reconstruction (AC-D11) — so the funnel's
 * closing band cannot disagree with the card above it. Not "agrees with": is.
 *
 * That also means the funnel FOLLOWS THE SCOPE FILTER for free, because everything it reads does. It does
 * not follow the period, for the same reason the ranking does not: the categories are a month's worth by
 * definition, and the section says which month it is showing.
 *
 * ==========================================================================================
 * WHAT THE SHAPE CANNOT SAY, AND WHERE IT IS SAID INSTEAD
 * ==========================================================================================
 *
 * A trapezoid has no negative width, so a household that overspent narrows to nothing and the bands after
 * that point are not drawn at all. AC-D12 forbids a deficit being shown as nothing — so the overspend is
 * stated here, in words, at the top of the section where the figure would have been.
 *
 * `overspendOf` rather than a subtraction written out here: the shape floors its bands using the same
 * function, so the sentence and the drawing cannot disagree about whether this month went over.
 */
export function IncomeFunnel({
  health,
  members,
  filters,
}: {
  health: DashboardHealthDto | undefined;
  members: readonly MemberDto[];
  filters: HealthFilterState;
}): ReactNode {
  const t = useT();
  const window = filterSummary(filters, members);

  /*
   * NOT DRAWN UNTIL BOTH SIDES EXIST, as the ring is not. With no income there is no top band to be a
   * proportion of, and with nothing recorded there is nothing to narrow. AC-D16's rule applies on top: a
   * funnel that consumed the household's entire expense total against no income would be arithmetically
   * correct and substantively false, and far more emphatic as a picture than as a figure.
   */
  const drawable = health !== undefined && health.emptiness === "none";

  const inCents = drawable ? BigInt(health.inCents) : 0n;
  const outCents = drawable ? BigInt(health.expenseSummary.totalCents) : 0n;

  const stages: FunnelStage[] = drawable
    ? health.expenseSummary.categories.map((row) => ({
        key: row.category,
        // From the shared catalogue rather than the payload, so the seventeen names live in one place —
        // the same lookup every picker, chip and ranking row uses.
        label: expenseCategoryLabel(row.category as ExpenseCategoryValue),
        cents: BigInt(row.monthlyCents),
      }))
    : [];

  const overspent = overspendOf({ inCents, outCents });

  return (
    <SectionCard
      title={t("dash.funnel.heading")}
      {...(drawable ? { value: formatEur(Number(inCents) / 100) } : {})}
      hint={
        health === undefined
          ? t("dash.health.reading")
          : overspent > 0n
            ? // THE FIGURE THE DRAWING CANNOT CARRY. First, and in the hint rather than buried in a
              // tooltip, because it is the most important thing this section can say about such a month.
              t("dash.funnel.overspentHint", {
                who: window,
                amount: formatEur(Number(overspent) / 100),
              })
            : t("dash.funnel.hint", { who: window })
      }
      testId="income-funnel"
    >
      <FunnelChart input={{ inCents, outCents, stages }} emptyMessage={t("dash.funnel.empty")} />
    </SectionCard>
  );
}
